import { useRef, useState, type FormEvent } from "react";
import type { CustomerFormDetail, FormAction, FormStatus } from "../../types/forms";
import { customerFormsApi } from "../../shared/customerFormsApi";
import { formActionLabels, statusLabels, today } from "../../shared/customerForms";
import { buttonClass, Field, FormInput, inputClass, secondaryClass } from "./FormControls";

export default function FormActionDialog({
  form,
  action,
  onDone,
  onClose,
  onBusy,
}: {
  form: CustomerFormDetail;
  action: FormStatus | "reschedule" | "reopen";
  onDone: (form: CustomerFormDetail) => void;
  onClose: () => void;
  onBusy: (value: boolean) => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [note, setNote] = useState("");
  const [date, setDate] = useState(
    action === "credit_issued" ? today() : form.scheduled_date || form.event_date || ""
  );
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const operation = useRef<{ signature: string; id: string } | undefined>(undefined);
  const locked = useRef(false);
  const [conflict, setConflict] = useState(false);
  const title =
    action === "reopen"
      ? "Reopen request"
      : action === "reschedule"
        ? "Reschedule Demo"
        : formActionLabels[action];
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (locked.current || conflict) return;
    const payload: FormAction = {
      version: form.version,
      operation_id: "",
      ...(!["reschedule", "reopen"].includes(action) ? { status: action as FormStatus } : {}),
      ...(action === "reopen" && form.status === "credit_issued"
        ? { acknowledge_credit_not_reversed: acknowledged }
        : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(action === "credit_issued"
        ? { credit_reference: reference.trim(), issued_date: date }
        : {}),
      ...(["scheduled", "reschedule"].includes(action) ? { scheduled_date: date } : {}),
    };
    const signature = JSON.stringify(payload);
    if (operation.current?.signature !== signature)
      operation.current = { signature, id: crypto.randomUUID() };
    payload.operation_id = operation.current.id;
    locked.current = true;
    setSaving(true);
    onBusy(true);
    setError("");
    const result =
      action === "reopen"
        ? await customerFormsApi.reopen(form.type, form.id, payload)
        : action === "reschedule"
          ? await customerFormsApi.reschedule(form.id, payload)
          : await customerFormsApi.transition(form.type, form.id, payload);
    locked.current = false;
    setSaving(false);
    onBusy(false);
    if (result.success) onDone(result.data);
    else {
      setError(
        result.errorStatus === 409
          ? "This action conflicts with a saved update. Close this dialog and refresh the form before trying again."
          : result.data.message || "Could not save. Retry with the same values."
      );
      setConflict(result.errorStatus === 409);
      setFieldErrors(result.data.errors || {});
    }
  };
  return (
    <form className="space-y-5" onSubmit={submit}>
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="text-sm text-gray-500">
        {form.reference} · {form.customer_name}
      </p>
      <fieldset disabled={saving || conflict} className="space-y-4">
        {action === "reopen" && (
          <div className="space-y-3 text-sm">
            <p>
              This request will return to{" "}
              <strong>
                {form.reopen_target_status
                  ? statusLabels[form.reopen_target_status]
                  : "its previous status"}
              </strong>
              . The closing event and your reason will remain in its history.
            </p>
            {form.status === "credit_issued" && (
              <label className="flex items-start gap-3 rounded-lg bg-amber-50 p-4 text-amber-900">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  required
                />
                <span>
                  I understand this corrects the recorded status and does not reverse an actual
                  credit. The current credit reference and issue date will be cleared and retained
                  in history.
                </span>
              </label>
            )}
          </div>
        )}
        {action === "credit_issued" && (
          <>
            <Field label="Credit memo / reference" error={fieldErrors.credit_reference?.[0]}>
              <FormInput
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={255}
                required
              />
            </Field>
            <Field label="Issue date" error={fieldErrors.issued_date?.[0]}>
              <FormInput
                type="date"
                max={today()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </Field>
            <p className="text-sm text-gray-500">
              Confirm the credit has already been issued. This action records it here.
            </p>
          </>
        )}
        {["scheduled", "reschedule"].includes(action) && (
          <Field label="Scheduled date" error={fieldErrors.scheduled_date?.[0]}>
            <FormInput
              type="date"
              min={today()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </Field>
        )}
        <Field
          label={
            ["rejected", "cancelled", "reschedule", "reopen"].includes(action)
              ? "Reason (required)"
              : "Note (optional)"
          }
          error={fieldErrors.note?.[0]}
        >
          <textarea
            className={inputClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={10000}
            required={["rejected", "cancelled", "reschedule", "reopen"].includes(action)}
          />
        </Field>
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-3">
        <button type="button" className={secondaryClass} onClick={onClose} disabled={saving}>
          Close
        </button>
        <button className={buttonClass} disabled={saving || conflict}>
          {saving ? "Saving…" : title}
        </button>
      </div>
    </form>
  );
}
